import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Send, Mail, MessageSquare, CheckCircle2 } from "lucide-react";
import { toast } from "../components/ui/sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Failed");
      setSent(true);
      toast("Message sent!", { description: "Our team will get back to you shortly." });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast("Something went wrong", { description: "Please try again in a moment." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="contact-page"
      className="min-h-screen bg-[#0A192F] text-slate-100 antialiased selection:bg-[#4CAF50] selection:text-[#04140a]"
    >
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="contact-logo-home" className="flex items-center">
            <img src="/geonetix-logo-full.webp" alt="GeoNetix" className="h-10 md:h-12 w-auto object-contain" />
          </Link>
          <Link
            to="/"
            data-testid="contact-back-home"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-slate-100 hover:border-[#00B4D8] hover:text-[#00B4D8] transition-colors duration-200"
          >
            <ArrowLeft size={16} /> Back to Home
          </Link>
        </div>
      </header>

      {/* Hero band */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 gx-grid-bg opacity-40" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-72 w-[70%] rounded-full bg-[#4CAF50]/10 blur-[120px]" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-8 py-16 md:py-20">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-mono-gx text-xs uppercase tracking-[0.25em] text-[#00B4D8] mb-4"
          >
            / Contact Us
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
          >
            Let&apos;s <span className="gx-text-gradient">talk.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 max-w-xl text-slate-300 leading-relaxed"
          >
            Questions about earnings, the dashcam, payouts or partnerships? Send us a
            message and our team will get back to you. For instant answers, try the
            Geo AI assistant in the corner.
          </motion.p>
        </div>
      </section>

      {/* Form */}
      <main className="max-w-4xl mx-auto px-6 md:px-8 py-14 md:py-16">
        <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-14">
          {/* Left info */}
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#00B4D8]/10 border border-[#00B4D8]/30">
                <Mail size={20} className="text-[#00B4D8]" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">Email us</h3>
                <a href="mailto:support@geonetix.live" className="text-sm text-slate-400 hover:text-[#00B4D8]">
                  support@geonetix.live
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#4CAF50]/10 border border-[#4CAF50]/30">
                <MessageSquare size={20} className="text-[#4CAF50]" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">Instant answers</h3>
                <p className="text-sm text-slate-400">
                  Use the Geo AI assistant (bottom-right) for 24/7 help.
                </p>
              </div>
            </div>
          </div>

          {/* Right form */}
          <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-6 md:p-8">
            {sent ? (
              <div data-testid="contact-success" className="flex flex-col items-center text-center py-10">
                <CheckCircle2 size={48} className="text-[#4CAF50] mb-4" />
                <h3 className="font-display font-bold text-2xl mb-2">Message sent!</h3>
                <p className="text-slate-400 max-w-sm">
                  Thanks for reaching out. Our team will get back to you at the email you provided.
                </p>
                <button
                  data-testid="contact-send-another"
                  onClick={() => setSent(false)}
                  className="mt-6 rounded-full border border-white/20 px-6 py-2.5 text-sm font-semibold hover:border-[#00B4D8] hover:text-[#00B4D8] transition-colors duration-200"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Name" required>
                    <input
                      data-testid="contact-name"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your name"
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Email" required>
                    <input
                      data-testid="contact-email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@example.com"
                      className={inputClass}
                    />
                  </Field>
                </div>
                <Field label="Subject">
                  <input
                    data-testid="contact-subject"
                    value={form.subject}
                    onChange={update("subject")}
                    placeholder="What's this about?"
                    className={inputClass}
                  />
                </Field>
                <Field label="Message" required>
                  <textarea
                    data-testid="contact-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    placeholder="Tell us how we can help..."
                    className={`${inputClass} resize-none`}
                  />
                </Field>
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={submitting}
                  className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-[#4CAF50] px-7 py-4 text-[15px] font-semibold text-[#04140a] hover:brightness-110 transition-[filter,transform] duration-200 hover:-translate-y-0.5 gx-green-glow disabled:opacity-60"
                >
                  <Send size={18} />
                  {submitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>

      {/* Bottom bar */}
      <footer className="border-t border-white/10 bg-[#050d1a]">
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src="/geonetix-logo-full.webp" alt="GeoNetix" className="h-9 w-auto object-contain" />
          <div className="text-xs text-slate-500">
            &copy; {new Date().getFullYear()} GeoNetix. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

const inputClass =
  "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00B4D8]/60 transition-colors duration-200";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="mb-2 block text-sm font-medium text-slate-300">
      {label} {required && <span className="text-[#4CAF50]">*</span>}
    </span>
    {children}
  </label>
);
